/**
 * Admin Users API Service
 * HTTP client methods for user management endpoints
 */

import { apiClient } from '@/core/api/client';
import type { PaginatedResponse } from '../types/pagination.type';
import type {
  AdminUser,
  CreateUserPayload,
  UpdateUserPayload,
  UserListParams,
  SendWelcomeEmailResponse,
} from '../types/admin-user.type';

const BASE_PATH = '/admin/users';

/**
 * List users with pagination and filters
 * GET /admin/users
 * 
 * @param params - Query parameters (page, pageSize, search, filters)
 * @returns Paginated list of users with total count reflecting filters
 */
export async function listUsers(
  params?: UserListParams,
): Promise<PaginatedResponse<AdminUser>> {
  const response = await apiClient.get<PaginatedResponse<AdminUser>>(
    BASE_PATH,
    { params },
  );
  return response.data;
}

/**
 * Get single user by ID
 * GET /admin/users/:id
 * 
 * @param id - User ID
 * @returns User details
 */
export async function getUser(id: string): Promise<AdminUser> {
  const response = await apiClient.get<AdminUser>(`${BASE_PATH}/${id}`);
  return response.data;
}

/**
 * Create new user
 * POST /admin/users
 * 
 * @param payload - User data
 * @returns Created user
 */
export async function createUser(
  payload: CreateUserPayload,
): Promise<AdminUser> {
  const response = await apiClient.post<AdminUser>(BASE_PATH, payload);
  return response.data;
}

/**
 * Update existing user
 * PATCH /admin/users/:id
 * 
 * @param id - User ID
 * @param payload - Partial user data to update
 * @returns Updated user
 */
export async function updateUser(
  id: string,
  payload: UpdateUserPayload,
): Promise<AdminUser> {
  const response = await apiClient.patch<AdminUser>(
    `${BASE_PATH}/${id}`,
    payload,
  );
  return response.data;
}

/**
 * Soft-delete user
 * DELETE /admin/users/:id
 * 
 * @param id - User ID
 * @returns void (204 No Content)
 */
export async function softDeleteUser(id: string): Promise<void> {
  await apiClient.delete(`${BASE_PATH}/${id}`);
}

/**
 * Send welcome email to professional (T074 - US8)
 * POST /admin/users/:id/send-welcome-email
 * 
 * @param id - User ID of the professional
 * @returns Success response with timestamp
 * @throws BadRequestException if user is not a professional
 * @throws NotFoundException if user not found
 */
export async function sendWelcomeEmail(
  id: string,
): Promise<SendWelcomeEmailResponse> {
  const response = await apiClient.post<SendWelcomeEmailResponse>(
    `${BASE_PATH}/${id}/send-welcome-email`,
  );
  return response.data;
}

/**
 * Default export for convenient importing
 */
export const adminUsersApi = {
  listUsers,
  getUser,
  createUser,
  updateUser,
  softDeleteUser,
  sendWelcomeEmail,
};
